<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import DialerAPI from 'dashboard/api/campaigns/dialer';

import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';

const props = defineProps({
  campaignId: { type: [String, Number], required: true },
  // Taking someone out is an administrator's call; everyone else only looks.
  canRemove: { type: Boolean, default: false },
  removingTaskId: { type: Number, default: null },
});

const emit = defineEmits(['remove']);

const { t } = useI18n();

// Must match the controller's page size, or the footer counts pages the server does not have.
const PAGE_SIZE = 50;
// A removed contact is left out of the queue by the server, so it is not offered as a filter.
const STATUSES = [
  'pending',
  'dialing',
  'connected',
  'completed',
  'no_answer',
  'voicemail',
  'failed',
  'exhausted',
];

const searchTerm = ref('');
const status = ref('');
const page = ref(1);
const tasks = ref([]);
const totalCount = ref(0);
const hasLoaded = ref(false);

const statusOptions = computed(() => [
  { value: '', label: t('CAMPAIGN.VOICE.DETAIL.LIST.ALL_STATUSES') },
  ...STATUSES.map(value => ({
    value,
    label: t(`CAMPAIGN.VOICE.DETAIL.LIST.STATUSES.${value.toUpperCase()}`),
  })),
]);

const hasFilters = computed(() => !!searchTerm.value.trim() || !!status.value);

// The screen polls while the campaign runs, and a poll answered after the agent typed would put
// the rows of the old search back. Only the latest request is allowed to land.
let latestRequest = 0;

const fetchQueue = async () => {
  latestRequest += 1;
  const requestId = latestRequest;
  try {
    const { data } = await DialerAPI.queue(props.campaignId, {
      page: page.value,
      status: status.value || undefined,
      q: searchTerm.value.trim() || undefined,
    });
    if (requestId !== latestRequest) return;

    // Taking out the last contact of the last page leaves the list on a page that no longer exists.
    const { total_pages: totalPages } = data.meta;
    if (totalPages && page.value > totalPages) {
      page.value = totalPages;
      return;
    }

    tasks.value = data.payload;
    totalCount.value = data.meta.count;
    hasLoaded.value = true;
  } catch (error) {
    // A transient failure keeps the rows on screen; the next poll picks it up.
  }
};

const applyFilters = () => {
  if (page.value === 1) fetchQueue();
  else page.value = 1;
};

// Vue's own watch around vueuse's debounce, rather than watchDebounced: in specs vueuse resolves a
// second copy of Vue, whose watch does not see this component's refs.
watch(searchTerm, useDebounceFn(applyFilters, 300));
watch(status, applyFilters);
watch(page, fetchQueue);
onMounted(fetchQueue);

// A call is on the line: this row is not one to take out.
const isInFlight = task => ['dialing', 'connected'].includes(task.status);

defineExpose({ refresh: fetchQueue });
</script>

<template>
  <div class="flex flex-col gap-3">
    <div
      v-if="hasFilters || totalCount"
      class="flex flex-col gap-2 sm:flex-row sm:items-center"
    >
      <Input
        v-model="searchTerm"
        type="search"
        :placeholder="t('CAMPAIGN.VOICE.DETAIL.LIST.SEARCH_PLACEHOLDER')"
        :custom-input-class="['ltr:!pl-8 rtl:!pr-8']"
        class="flex-1"
      >
        <template #prefix>
          <Icon
            icon="i-lucide-search"
            class="absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:left-2 rtl:right-2"
          />
        </template>
      </Input>
      <Select
        v-model="status"
        :options="statusOptions"
        :aria-label="t('CAMPAIGN.VOICE.DETAIL.LIST.STATUS')"
      />
    </div>

    <div
      v-if="tasks.length"
      class="overflow-hidden border rounded-xl border-n-weak"
    >
      <table class="w-full text-sm table-fixed">
        <colgroup>
          <col />
          <col class="w-44" />
          <col class="w-36" />
          <col class="w-28" />
          <col class="w-40" />
          <col class="w-12" />
        </colgroup>
        <thead>
          <tr class="bg-n-alpha-1 text-n-slate-11">
            <th class="px-4 py-2 font-medium text-left">
              {{ t('CAMPAIGN.VOICE.DETAIL.LIST.CONTACT') }}
            </th>
            <th class="px-4 py-2 font-medium text-left">
              {{ t('CAMPAIGN.VOICE.DETAIL.LIST.PHONE') }}
            </th>
            <th class="px-4 py-2 font-medium text-left">
              {{ t('CAMPAIGN.VOICE.DETAIL.LIST.STATUS') }}
            </th>
            <th class="px-4 py-2 font-medium text-right">
              {{ t('CAMPAIGN.VOICE.DETAIL.LIST.ATTEMPTS') }}
            </th>
            <th class="px-4 py-2 font-medium text-left">
              {{ t('CAMPAIGN.VOICE.DETAIL.LIST.AGENT') }}
            </th>
            <th class="px-2 py-2" />
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="task in tasks"
            :key="task.id"
            class="border-t border-n-weak"
          >
            <td class="px-4 py-2 truncate text-n-slate-12">
              {{ task.name }}
            </td>
            <td class="px-4 py-2 truncate text-n-slate-11">
              {{ task.phone_number }}
            </td>
            <td class="px-4 py-2 truncate text-n-slate-11">
              {{
                t(
                  `CAMPAIGN.VOICE.DETAIL.LIST.STATUSES.${task.status.toUpperCase()}`
                )
              }}
            </td>
            <td class="px-4 py-2 text-right tabular-nums text-n-slate-11">
              {{ task.attempts }}
            </td>
            <td class="px-4 py-2 truncate text-n-slate-11">
              {{ task.agent || '—' }}
            </td>
            <td class="px-2 py-2 text-right">
              <Button
                v-if="canRemove && !isInFlight(task)"
                icon="i-lucide-x"
                variant="ghost"
                color="slate"
                size="xs"
                :aria-label="t('CAMPAIGN.VOICE.DETAIL.LIST.REMOVE')"
                :is-loading="removingTaskId === task.id"
                @click="emit('remove', task)"
              />
            </td>
          </tr>
        </tbody>
      </table>
      <PaginationFooter
        v-if="totalCount > PAGE_SIZE"
        v-model:current-page="page"
        :total-items="totalCount"
        :items-per-page="PAGE_SIZE"
        current-page-info="CAMPAIGN.VOICE.DETAIL.LIST.PAGINATION"
      />
    </div>

    <p
      v-else-if="hasLoaded && hasFilters"
      class="px-4 py-8 m-0 text-sm text-center border rounded-xl border-n-weak text-n-slate-11"
    >
      {{ t('CAMPAIGN.VOICE.DETAIL.LIST.NO_RESULTS') }}
    </p>
  </div>
</template>
